import {
  Container,
  Flex,
  Paper,
  Text,
  Badge,
  Tabs,
  Anchor,
  Title,
  Box,
  Button,
  Center,
  Loader,
  Stack,
} from "@mantine/core";
import {
  IconBuilding,
  IconMapPin,
  IconPhone,
  IconMail,
  IconWorld,
  IconExternalLink,
  IconArrowLeft,
  IconEye,
} from "@tabler/icons-react";
import {
  NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
  NAV_TABS_FULL_WIDTH_LINE_STYLE,
  navTabsListStyles,
} from "@/shared/styles/navTabsLayout";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { getFileUrl } from "@/shared/utils/useFileUrl";
import CollaborationDocumentsTab from "./DocumentRoom";
import CollaborationLogHistoryTab from "./LogHistoryTable";
import { formatHeadquartersLocationLine } from "@/shared/constants/signup.const";

type CompanyDetails = {
  id: string;
  name: string | null;
  gtid: string;
  headquartersCity: string | null;
  headquartersCountry: string | null;
  phoneCountryCode: string | null;
  phoneNumber: string | null;
  website: string | null;
  email: string | null;
  logoId: string | null;
};

type SharedDocument = {
  id: string;
  sharedAt: Date | string;
  downloadCount: number;
  file: {
    id: string;
    filename: string;
    name: string | null;
    mimeType: string | null;
    size: number;
  };
  sharedBy: {
    id: string;
    legalName: string | null;
    gtid: string;
  };
};

type CollaborationDetail = {
  collaborationId: string;
  requesterDetails: CompanyDetails;
  receiverDetails: CompanyDetails;
  sharedDocuments: SharedDocument[];
};

interface CompanyCardProps {
  company: CompanyDetails;
  label: string;
  onViewProfile: () => void;
}

function CompanyCard({ company, label, onViewProfile }: CompanyCardProps) {
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    if (company?.logoId) {
      getFileUrl(company.logoId).then((url) => {
        setLogoUrl(url);
      });
    }
  }, [company]);

  return (
    <Paper p="xl" radius="lg" style={{ border: "1px solid #E5E7EB" }}>
      {/* Label Badge */}
      <Badge
        variant="light"
        size="sm"
        radius="sm"
        mb="md"
        style={{
          backgroundColor: label === "Requester" ? "#EBF5FF" : "#F0FDF4",
          color: label === "Requester" ? "#1D4ED8" : "#15803D",
        }}
      >
        {label}
      </Badge>

      <Flex justify="space-between" align="flex-start">
        <Flex gap="md" align="flex-start">
          <Flex
            w={64}
            h={64}
            style={{
              borderRadius: 8,
              border: "1px solid #E5E7EB",
              backgroundColor: "#F9FAFB",
              flexShrink: 0,
            }}
            align="center"
            justify="center"
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Company Logo"
                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }}
              />
            ) : (
              <IconBuilding size={28} color="#6B7280" />
            )}
          </Flex>

          <Flex direction="column" gap={4}>
            {/* Row 1: Company Name */}
            <Flex align="center" gap="xs">
              <Title order={4} fw={500}>
                {company.name || "Company Name"}
              </Title>
            </Flex>

            {/* Row 2: GTID Badge */}
            {company.gtid && (
              <Badge
                variant="light"
                size="sm"
                radius="sm"
                style={{
                  width: "fit-content",
                  backgroundColor: "#EEF2F7",
                  color: "#374151",
                }}
              >
                {company.gtid}
              </Badge>
            )}

            {/* Row 3: Location, Phone, Email */}
            <Flex gap="md" align="center" mt={4} wrap="wrap">
              <Flex align="center" gap={6}>
                <IconMapPin size={16} color="#6B7280" />
                <Text size="sm" c="slate.6">
                  {formatHeadquartersLocationLine(company.headquartersCity, company.headquartersCountry)}
                </Text>
              </Flex>
              <Flex align="center" gap={6}>
                <IconPhone size={16} color="#6B7280" />
                <Text size="sm" c="slate.6">
                  {company.phoneCountryCode && company.phoneNumber
                    ? `${company.phoneCountryCode} ${company.phoneNumber}`
                    : "N/A"}
                </Text>
              </Flex>
              <Flex align="center" gap={6}>
                <IconMail size={16} color="#6B7280" />
                <Text size="sm" c="slate.6">
                  {company.email || "N/A"}
                </Text>
              </Flex>
            </Flex>

            {/* Row 4: Website */}
            <Flex gap="md" align="center" mt={2}>
              {company.website && (
                <Anchor href={company.website} target="_blank" td="none">
                  <Flex align="center" gap={6}>
                    <IconWorld size={16} color="var(--mantine-color-primary-6)" />
                    <Text size="sm" c="primary.6">
                      Website
                    </Text>
                    <IconExternalLink size={14} color="var(--mantine-color-primary-6)" />
                  </Flex>
                </Anchor>
              )}
            </Flex>
          </Flex>
        </Flex>

        {/* Right side: View Profile Button */}
        <Button variant="outline" leftSection={<IconEye size={16} />} onClick={onViewProfile}>
          View Profile
        </Button>
      </Flex>
    </Paper>
  );
}

export default function AdminCollaborationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [collaboration, setCollaboration] = useState<CollaborationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string | null>("overview");

  useEffect(() => {
    if (!id) return;

    const fetchCollaborationData = async () => {
      setLoading(true);
      try {
        const data = await trpc.admin.getCollaborationDetails.query({ matchId: id });
        setCollaboration(data);
      } catch (err) {
        console.error("Error fetching collaboration:", err);
        handleTrpcError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCollaborationData();
  }, [id]);

  if (loading) {
    return (
      <Center h="50vh">
        <Loader size="lg" variant="dots" />
      </Center>
    );
  }

  if (!collaboration) {
    return (
      <Container size="fluid" py="xl">
        <Text>Collaboration not found</Text>
      </Container>
    );
  }

  return (
    <Box>
      {/* Back Button */}
      <Box py="sm" px="xl">
        <Button
          variant="subtle"
          color="gray"
          leftSection={<IconArrowLeft size={16} />}
          size="sm"
          onClick={() => navigate("/admin/collaborations")}
        >
          Back to Collaborations
        </Button>
      </Box>

      <Container size="fluid" px="xl">
        {/* Page Title */}
        <Flex align="center" gap="md" mb="xl">
          <Title order={2} fw={400}>
            Collaboration Details
          </Title>
        </Flex>

        {/* Tabs Section */}
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Box w="100%" style={NAV_TABS_FULL_WIDTH_LINE_STYLE}>
            <Tabs.List styles={navTabsListStyles(3)}>
              <Tabs.Tab
                value="overview"
                px="md"
                style={{
                  ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
                  color: activeTab === "overview" ? "var(--mantine-color-primary-7)" : "var(--mantine-color-slate-6)",
                }}
              >
                Overview
              </Tabs.Tab>
              <Tabs.Tab
                value="documents"
                px="md"
                style={{
                  ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
                  color: activeTab === "documents" ? "var(--mantine-color-primary-7)" : "var(--mantine-color-slate-6)",
                }}
              >
                Documents
              </Tabs.Tab>
              <Tabs.Tab
                value="history"
                px="md"
                style={{
                  ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
                  color: activeTab === "history" ? "var(--mantine-color-primary-7)" : "var(--mantine-color-slate-6)",
                }}
              >
                Log History
              </Tabs.Tab>
            </Tabs.List>
          </Box>

          {/* Overview Tab */}
          <Tabs.Panel value="overview" pt="xl">
            <Stack gap="xl">
              <CompanyCard
                company={collaboration.requesterDetails}
                label="Requester"
                onViewProfile={() => navigate(`/admin/companies/${collaboration.requesterDetails.id}`)}
              />
              <CompanyCard
                company={collaboration.receiverDetails}
                label="Requestee"
                onViewProfile={() => navigate(`/admin/companies/${collaboration.receiverDetails.id}`)}
              />
            </Stack>
          </Tabs.Panel>

          {/* Documents Tab */}
          <Tabs.Panel value="documents" pt="xl">
            <CollaborationDocumentsTab sharedDocuments={collaboration.sharedDocuments} />
          </Tabs.Panel>

          {/* Log History Tab */}
          <Tabs.Panel value="history" pt="xl">
            <CollaborationLogHistoryTab matchId={collaboration.collaborationId} />
          </Tabs.Panel>
        </Tabs>
      </Container>
    </Box>
  );
}
